// Shared plumbing for the external CPMS (Charge Point Management System)
// service, reached via `cpmsApi` (BackendType.CPMS). Endpoints are relative to
// VITE_CPMS_BACKEND_URL, which already includes the `/cpms` base.
//
// Several modules read the same CPMS collections; the envelope, the page
// drain and the charging-station fleet query live here so they share one
// implementation and one react-query cache entry (no cross-module imports).

import { AxiosError } from 'axios';
import { cpmsApi } from './client';

/** Spring-style paginated response envelope returned by every CPMS list endpoint. */
export interface CpmsPage<T> {
  content: T[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Records per request when reading a whole CPMS collection. This is the largest
 * page CPMS serves; asking for fewer only multiplies the round trips.
 */
export const FLEET_PAGE_SIZE = 2000;

/** Runaway guard: 100 pages (200k records at `FLEET_PAGE_SIZE`). Normal termination is `totalPages`. */
const MAX_PAGES = 100;

/**
 * React Query retry policy for CPMS calls: never retry an auth failure (the
 * axios interceptor already attempts a token refresh), otherwise retry once.
 */
export function cpmsRetry(failureCount: number, error: Error): boolean {
  if (error instanceof AxiosError && error.response?.status === 401) return false;
  return failureCount < 1;
}

/** The slice of the CPMS envelope the page drain reads. */
interface CpmsPageSlice<T> {
  content: T[];
  totalPages: number;
}

/**
 * Read every page of a CPMS list endpoint: fetch page 0 for `totalPages`, then
 * pull the remainder concurrently (a serial walk would grow latency with the
 * collection). Bounded by `MAX_PAGES` so a bad `totalPages` can't fan out
 * without limit — the result is truncated and a warning logged instead.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<CpmsPageSlice<T>>,
): Promise<T[]> {
  const first = await fetchPage(0);
  if (first.totalPages > MAX_PAGES) {
    console.warn(`CPMS reported ${first.totalPages} pages; truncating the drain at ${MAX_PAGES}`);
  }
  const lastPage = Math.min(first.totalPages, MAX_PAGES);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, lastPage - 1) }, (_, index) => fetchPage(index + 1)),
  );
  return [first, ...rest].flatMap((page) => page.content ?? []);
}

// ============================================================================
// Charging stations — the one fleet download shared by every consumer.
// ============================================================================

interface CpmsConnector {
  connectorId: number;
  maxElectricPower: number;
  connectorType: string;
  status: string;
  availability: string;
  errorCode: string;
  errorInfo: string;
}

interface CpmsEvse {
  id: number;
  evseId: string;
  status: string;
  subStatus: string;
  connectors: CpmsConnector[];
  availability: string;
}

interface CpmsChargingStationAttributes {
  vendorId: string;
  model: string;
  firmwareVersion: string;
  chargingStationSerialNumber: string;
}

/**
 * A CPMS charging station: the superset of the fields the consuming modules
 * read (cpo-analytics reads all of it; helpdesk reads id/location/EVSEs/
 * connector types; qr-payment reads id/locationName). Consumers `select` the
 * subset they need — see `chargingStationsQueryOptions`.
 */
export interface CpmsChargingStation {
  id: string;
  protocol: string;
  locationId: string;
  locationName: string;
  availability: string;
  status: string;
  evses: CpmsEvse[];
  attributes: CpmsChargingStationAttributes;
  lastSeen: string;
  customIdentifier: string;
}

const CHARGING_STATIONS_ENDPOINT = '/rest/operator-api/charging-stations';

/**
 * Query options for the complete CPMS charging-station fleet. Every module that
 * needs the fleet (cpo-analytics dashboard, helpdesk station picker, qr-payment
 * link dialog) spreads these into its own `useQuery` with a module-specific
 * `select`/`enabled` — so visiting all three screens downloads the fleet once
 * under the single `['cpms', 'charging-stations']` cache key instead of three
 * times under three keys.
 *
 * Every page is read up front (concurrently, via `fetchAllPages`) because each
 * consumer needs the whole collection before the data is usable. `staleTime`
 * pins the pre-existing 5-minute window all consumers already had, independent
 * of the app-wide default.
 */
export function chargingStationsQueryOptions() {
  return {
    queryKey: ['cpms', 'charging-stations'] as const,
    queryFn: () =>
      fetchAllPages((page) =>
        cpmsApi
          .get<CpmsPage<CpmsChargingStation>>(CHARGING_STATIONS_ENDPOINT, {
            params: { page, size: FLEET_PAGE_SIZE, sort: 'id,asc' },
          })
          .then((response) => response.data),
      ),
    staleTime: 5 * 60_000,
    retry: cpmsRetry,
  } as const;
}
